import {
    fetchPlanWallet,
    isModeUnknownError,
    planSessionToken,
    type PlanWallet,
} from "../api/supPlanApi";
import { SUP_PLANS_SITE_ON } from "./config";

/**
 * Вид кошелька пакетов — один на вкладку (TECH_PLAN §10.1 usePlanWallet).
 *
 * Одно чтение в пути на вкладку; свежий вид живёт минуту (у пакетов) или пять
 * (у «tokens» и гостя — им сервер ничего не покажет). Ответы трат поиска
 * (sup-spend, свой вопрос) несут вид тоже — он подменяет этот, если новее
 * (`seq`), а цены и «подписчик» остаются из GET /wallet: только он их считает.
 *
 * «Кошелёк не пришёл ≠ токены». Кто хоть раз был в режиме пакетов, при сбое
 * чтения не уходит на старые токены (их файл для него мёртв): чтение
 * отвечает ошибкой, страница ждёт и переспрашивает. То же, когда сервер сам
 * сказал, что не знает режима (503 / 409 — isModeUnknownError).
 */

export const PLAN_CHANGED_EVENT = "sup-plan-changed";

const TTL_PLANS_MS = 60 * 1000;
const TTL_OTHER_MS = 5 * 60 * 1000;
const MODE_KEY = "sup_plan_mode";

interface StoreState {
    token: string;
    wallet: PlanWallet | null;
    at: number;
}

let state: StoreState = { token: "", wallet: null, at: 0 };
let inflight: { token: string; visit: boolean; promise: Promise<PlanWallet | null> } | null = null;
// Сервер не ответил тому, кто не на пакетах (например, сервер пакетов ещё не
// выложен): минуту не переспрашиваем — иначе лишний запрос на каждое чтение кошелька.
let failed: { token: string; until: number } | null = null;
const FAIL_PAUSE_MS = 60 * 1000;

const who = (): string => {
    try {
        return (localStorage.getItem("site_user_email") || "").trim().toLowerCase() || "?";
    } catch {
        return "?";
    }
};

const rememberMode = (plans: boolean): void => {
    try {
        if (plans) localStorage.setItem(MODE_KEY, JSON.stringify({ u: who(), m: "plans" }));
        else localStorage.removeItem(MODE_KEY);
    } catch {
        /* без localStorage — только память вкладки */
    }
};

/** Этот человек уже был в режиме пакетов (на этом устройстве). */
export const rememberedPlans = (): boolean => {
    try {
        const raw = localStorage.getItem(MODE_KEY);
        if (!raw) return false;
        const v = JSON.parse(raw) as { u?: string; m?: string };
        return v.m === "plans" && v.u === who();
    } catch {
        return false;
    }
};

const currentLang = (): string => {
    try {
        return window.location.pathname.split("/")[1] || "ru";
    } catch {
        return "ru";
    }
};

const announce = (): void => {
    try {
        window.dispatchEvent(new Event(PLAN_CHANGED_EVENT));
    } catch {
        /* нет окна */
    }
};

// ── Кому видно новое 11.10 (владелец 12.10, решение 1): лекции в умном поиске, клипы под
// воротами, месяц в подарок за покупку — только тем, кому сервер отдаёт `lecturesGate: true`
// (sup_plan.feature_public: пока выключатель не «all» — админы и тестеры). Один переход
// выключателя на сервере открывает всё сразу — сайт ничего не выкладывает заново.
// Последний ответ помним на устройстве: у кого ворот нет, лекция открывается сразу, как до
// 11.10, а не после запроса.
const GATE_KEY = "sup_lectures_gate";
let guestGate: boolean | null = null;

const gateOwner = (): string => (planSessionToken() ? who() : "?");

const rememberGate = (on: boolean): void => {
    try {
        localStorage.setItem(GATE_KEY, JSON.stringify({ u: gateOwner(), g: on }));
    } catch {
        /* без localStorage — только память вкладки */
    }
};

const rememberedGate = (): boolean | null => {
    try {
        const raw = localStorage.getItem(GATE_KEY);
        if (!raw) return null;
        const v = JSON.parse(raw) as { u?: string; g?: unknown };
        return v.u === gateOwner() && typeof v.g === "boolean" ? v.g : null;
    } catch {
        return null;
    }
};

/** Последний известный вид для текущего сеанса (или null — ещё не читали). */
export const getPlanWallet = (): PlanWallet | null => {
    const token = planSessionToken();
    return token && state.token === token ? state.wallet : null;
};

/** Человек сейчас в режиме пакетов (вид пришёл — по нему; нет — по памяти устройства). */
export const isPlansModeNow = (): boolean => {
    if (!SUP_PLANS_SITE_ON || !planSessionToken()) return false;
    const w = getPlanWallet();
    if (w) return w.mode === "plans";
    return rememberedPlans();
};

/**
 * Принять вид от сервера. `partial` — вид из ответа траты: там цены обычные и
 * «подписчик» не проверен — их берём из последнего GET /wallet.
 */
export const setPlanWallet = (next: PlanWallet, opts: { partial?: boolean } = {}): void => {
    const token = planSessionToken();
    if (!token) return;
    const cur = getPlanWallet();
    let w = next;
    if (cur && cur.mode === "plans" && next.mode === "plans") {
        if ((cur.seq || 0) > (next.seq || 0) && (next.seq || 0) > 0) return;
        if (opts.partial) w = { ...next, subscriber: cur.subscriber, offers: cur.offers };
    }
    state = { token, wallet: w, at: Date.now() };
    rememberMode(w.mode === "plans");
    rememberGate(w.lecturesGate === true);
    announce();
};

/**
 * Лекции и клипы этому человеку — под воротами умного поиска? По тому, что уже известно, без
 * запроса: вид этого сеанса (любой режим), вид гостя, ответ, запомненный на устройстве.
 * null — ещё не знаем.
 */
export const knownLecturesGate = (): boolean | null => {
    if (!SUP_PLANS_SITE_ON) return false;
    if (planSessionToken()) {
        const w = getPlanWallet();
        if (w) return w.lecturesGate === true;
    } else if (guestGate !== null) {
        return guestGate;
    }
    return rememberedGate();
};

/** Видно ли новое 11.10 (тексты «клипы и лекции», правила): не знаем — нет, как до 11.10. */
export const lecturesGateOn = (): boolean => knownLecturesGate() === true;

/** Месяц в подарок ждёт первого захода в поиск — заходу нужен свежий вид с visit=1. */
const buyerWaits = (w: PlanWallet | null | undefined): boolean => w?.mode === "plans" && !!w.buyerMonth?.pending;

/**
 * Прочитать вид (кэш, одно чтение в пути). null — режим не пакетов или выключатель сайта.
 * `visit` — заход в умный поиск (СУП, лекции): ждущий месяц в подарок начинается. Вид, взятый
 * без захода (меню, витрины), годится и заходу — кроме случая, когда в нём подарок ждёт: тогда
 * читаем ещё раз, уже с visit=1, и месяц начинается.
 */
export const loadPlanWallet = (
    opts: { fresh?: boolean; force?: boolean; visit?: boolean } = {},
): Promise<PlanWallet | null> => {
    if (!SUP_PLANS_SITE_ON) return Promise.resolve(null);
    const token = planSessionToken();
    if (!token) return Promise.resolve(null);
    const cur = getPlanWallet();
    const visit = !!opts.visit;
    const again = opts.fresh || opts.force;
    if (
        cur &&
        !again &&
        !(visit && buyerWaits(cur)) &&
        Date.now() - state.at < (cur.mode === "plans" ? TTL_PLANS_MS : TTL_OTHER_MS)
    ) {
        return Promise.resolve(cur);
    }
    if (inflight && inflight.token === token && !again) {
        if (!visit || inflight.visit) return inflight.promise;
        // В пути — чтение без захода: дождаться его; подарок ждёт — прочитать с заходом.
        return inflight.promise.then((w) => (buyerWaits(w) ? loadPlanWallet({ force: true, visit: true }) : w));
    }
    if (!cur && !again && failed && failed.token === token && Date.now() < failed.until && !rememberedPlans()) {
        return Promise.resolve(null);
    }
    const promise: Promise<PlanWallet | null> = fetchPlanWallet({ fresh: opts.fresh, lang: currentLang(), visit })
        .then((w) => {
            failed = null;
            if (planSessionToken() === token) setPlanWallet(w);
            return w;
        })
        .catch((e) => {
            const known = getPlanWallet();
            if (known) return known;
            if (rememberedPlans() || isModeUnknownError(e)) throw e;
            failed = { token, until: Date.now() + FAIL_PAUSE_MS };
            return null;
        })
        .finally(() => {
            if (inflight?.promise === promise) inflight = null;
        });
    inflight = { token, visit, promise };
    return promise;
};

/**
 * Быстрая проверка режима для путей ТОКЕНОВ (кошелёк поиска, меню, курс): кто на этом
 * устройстве уже был на пакетах или чей вид уже прочитан — ждёт обычного чтения; остальным
 * сервер пакетов отвечает за `timeoutMs` (2,5 с), иначе — «не пакеты» (null), и их токены
 * не ждут медленный site_server. Чтение при этом продолжается: пришёл вид «plans» — страницы
 * узнают по PLAN_CHANGED_EVENT. Ошибку «режим неизвестен» отдаёт наружу (страница не
 * рисует токены).
 */
export const MODE_CHECK_MS = 2500;

export const loadPlanModeQuick = (
    timeoutMs: number = MODE_CHECK_MS,
    opts: { visit?: boolean } = {},
): Promise<PlanWallet | null> => {
    const full = loadPlanWallet({ visit: opts.visit });
    if (rememberedPlans() || getPlanWallet()) return full;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<PlanWallet | null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
    });
    // Опоздавшая ошибка не должна стать «Uncaught (in promise)».
    full.catch(() => undefined);
    return Promise.race([full, late]).finally(() => clearTimeout(timer));
};

/** Что даст вход гостю (вид гостя кэшируется на вкладку). */
let guestInfo: Promise<PlanWallet | null> | null = null;

export const loadGuestPlanInfo = (): Promise<PlanWallet | null> => {
    if (!SUP_PLANS_SITE_ON || planSessionToken()) return Promise.resolve(null);
    if (!guestInfo) {
        guestInfo = fetchPlanWallet({ lang: currentLang() })
            .then((w) => {
                if (!planSessionToken()) {
                    guestGate = w.lecturesGate === true;
                    rememberGate(guestGate);
                }
                return w;
            })
            .catch(() => {
                guestInfo = null;
                return null;
            });
    }
    return guestInfo;
};

/**
 * Узнать у сервера, под воротами ли лекции и клипы: вошедшему — вид кошелька (любой режим,
 * без захода в поиск), гостю — вид гостя (выключатель «all»). Не бросает: сбой чтения (сеть,
 * 503 wallet_unavailable, 409, в том числе у 299 перенесённых на пакеты — их чтение
 * loadPlanWallet отдаёт ошибкой) и ответ дольше `timeoutMs` (2,5 с — как быстрая проверка
 * режима) — «нет», как до 11.10: лучше бесплатно, чем «Не удалось загрузить» у того, кому
 * ворот не положено. Чтение при этом продолжается: пришёл вид — ответ запомнен на устройстве
 * (rememberGate), следующая лекция решит по нему. Кому ворота известны (запомнены «да»), сюда
 * не приходит — у них сбой кошелька по-прежнему «Повторить».
 */
export const loadLecturesGate = async (timeoutMs: number = MODE_CHECK_MS): Promise<boolean> => {
    if (!SUP_PLANS_SITE_ON) return false;
    const ask = (async (): Promise<boolean> => {
        if (planSessionToken()) {
            const w = await loadPlanWallet();
            return !!w && w.lecturesGate === true;
        }
        const g = await loadGuestPlanInfo();
        return !!g && g.lecturesGate === true;
    })().catch(() => false);
    if (!(timeoutMs > 0)) return ask;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(false), timeoutMs);
    });
    return Promise.race([ask, late]).finally(() => clearTimeout(timer));
};

/**
 * Трата поиска получила 503 wallet_unavailable: сервер не смог записать — открытого нет
 * (не «бесплатно» и не «нет пакета»). Страница поиска, к которой это придёт как «не пустил»,
 * показывает «Не удалось загрузить · Повторить», а не окно пакетов.
 */
let unavailableAt = 0;
const UNAVAILABLE_FRESH_MS = 10 * 1000;

export const notePlanUnavailable = (): void => {
    unavailableAt = Date.now();
};

/** Был ли такой отказ только что (не забывая его). */
export const planUnavailableNow = (): boolean => Date.now() - unavailableAt < UNAVAILABLE_FRESH_MS;

/** Был ли такой отказ только что — и забыть его (одно «не пустил» — одна строка). */
export const takePlanUnavailable = (): boolean => {
    const v = planUnavailableNow();
    unavailableAt = 0;
    return v;
};

/**
 * Старая оплата токенов ответила 410 (человек уже на пакетах или выключатель «all»):
 * перечитать режим мимо кэша. true — пакеты (страница открывает окно пакетов).
 */
export const recheckPlansMode = async (): Promise<boolean> => {
    try {
        const w = await loadPlanWallet({ force: true });
        return w?.mode === "plans";
    } catch {
        return isPlansModeNow();
    }
};

/** Подписка на «вид поменялся»; возвращает отписку. */
export const onPlanChanged = (cb: () => void): (() => void) => {
    window.addEventListener(PLAN_CHANGED_EVENT, cb);
    return () => window.removeEventListener(PLAN_CHANGED_EVENT, cb);
};

/** Для тестов и выхода: забыть вид и чтение в пути. */
export const forgetPlanWallet = (): void => {
    state = { token: "", wallet: null, at: 0 };
    inflight = null;
    failed = null;
    guestInfo = null;
    guestGate = null;
    unavailableAt = 0;
};
